import { Box, Divider, List, Paper, Typography } from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { map, orderBy as orderByLodash } from "lodash";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import UploadInternacionalDb from "../../../../db/uploadInternacionalDb";
import UploadInternacional from "../../../../models/uploadInternacional";
import { SituacaoUpload } from "../../../../models/enum";
import StatementImportado from "./statementImportado";
import StatementImportando from "./statementImportando";
import StatementComErro from "./statementComErro";
import { radius } from "../../../../theme";

export default function ListaStatements() {
  const { visualizado } = useUsuario();
  const [uploads, setUploads] = useState<UploadInternacional[]>([]);
  const [uploadsImportados, setUploadsImportados] = useState<
    UploadInternacional[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const [limitItens, setLimit] = useState(20);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new UploadInternacionalDb().collection({
            usuarioId: visualizado,
            uploadId: "",
          }),
          where("situacao", "!=", SituacaoUpload.IMPORTADO)
        ),
        (snapshot) => setUploads(map(snapshot.docs, (doc) => doc.data()))
      );
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new UploadInternacionalDb().collection({
            usuarioId: visualizado,
            uploadId: "",
          }),
          where("situacao", "==", SituacaoUpload.IMPORTADO),
          orderBy("dataUpload", "desc"),
          limit(limitItens)
        ),
        (snapshot) => {
          const uploadsCarregados = map(snapshot.docs, (doc) => doc.data());
          if (uploadsCarregados.length === uploadsImportados.length) {
            setHasMore(false);
          }
          setUploadsImportados(uploadsCarregados);
        }
      );
  }, [visualizado, limitItens]);

  const carregarMaisItens = () => {
    if (!hasMore || !!!visualizado) return;
    setLimit((prev) => prev + 20);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          carregarMaisItens();
        }
      },
      { threshold: 1.0 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, uploadsImportados]);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${radius.xl}px`, overflow: "hidden" }}
    >
      <List sx={{ py: 0 }}>
        {map(
          orderByLodash(
            [...uploads, ...uploadsImportados],
            [
              (u) => {
                switch (u.situacao) {
                  case SituacaoUpload.IMPORTADO:
                    return 2;
                  case SituacaoUpload.ERRO_AO_IMPORTAR:
                  case SituacaoUpload.ERRO_AO_LER:
                    return 1;
                  case SituacaoUpload.PENDENTE:
                  case SituacaoUpload.SALVO:
                  default:
                    return 0;
                }
              },
              (u) => u.periodo?.de.unix() ?? 0,
            ],
            ["asc", "desc"]
          ),
          (upload, index) => {
            let componente;
            switch (upload.situacao) {
              case SituacaoUpload.IMPORTADO:
                componente = (
                  <StatementImportado key={upload.id} upload={upload} />
                );
                break;
              case SituacaoUpload.SALVO:
              case SituacaoUpload.PENDENTE:
                componente = (
                  <StatementImportando key={upload.id} upload={upload} />
                );
                break;
              case SituacaoUpload.ERRO_AO_IMPORTAR:
              case SituacaoUpload.ERRO_AO_LER:
              default:
                componente = (
                  <StatementComErro key={upload.id} upload={upload} />
                );
                break;
            }
            return (
              // Hairline entre itens (DESIGN.md → Elevation): separa a partir do
              // segundo item, para a lista inteira e não só o primeiro bloco.
              <Fragment key={upload.id}>
                {index > 0 && <Divider variant="fullWidth" />}
                {componente}
              </Fragment>
            );
          }
        )}
      </List>
      {hasMore && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          width="100%"
          paddingY={2}
        >
          <div ref={loaderRef}>
            <Typography variant="labelMono" color="text.secondary">
              Carregando mais itens...
            </Typography>
          </div>
        </Box>
      )}
    </Paper>
  );
}
